// Shared renderer for the legal/policy pages (cookie- og privatlivspolitik,
// persondatapolitik) recreated on the old WordPress URLs after the migration.
// A legal page is just a titled prose article: a heading, an intro and an
// ordered list of prose blocks (headings / paragraphs / lists).

import { SiteLayout } from "@/components/SiteLayout";
import { type Locale } from "@/i18n/config";
import { pageHead, type PageMeta } from "@/i18n/head";
import { type PageId } from "@/i18n/slugMap";

/** One prose block in a legal page body. Mirrors the ArticlePage prose model. */
export type LegalBlock =
  | { type: "heading"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] };

export type LegalStrings = {
  meta: PageMeta;
  title: string;
  intro: string;
  blocks: LegalBlock[];
};

/** <head> for a legal page: pageHead with the page title as the breadcrumb name. */
export function legalHead(pageId: PageId, locale: Locale, meta: PageMeta) {
  return pageHead(pageId, locale, meta, { breadcrumbName: meta.title });
}

export function LegalPage({ title, intro, blocks }: Omit<LegalStrings, "meta">) {
  return (
    <SiteLayout>
      <article className="mx-auto max-w-3xl px-4 md:px-8 py-16 md:py-24">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">{title}</h1>
        <p className="mt-6 text-lg leading-relaxed text-foreground/85">{intro}</p>

        <div className="mt-10 space-y-6">
          {blocks.map((b, i) => {
            switch (b.type) {
              case "heading":
                return (
                  <h2 key={i} className="mt-10 text-2xl md:text-3xl font-bold tracking-tight">
                    {b.text}
                  </h2>
                );
              case "paragraph":
                return (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/85">
                    {b.text}
                  </p>
                );
              case "list":
                return (
                  <ul key={i} className="space-y-2 pl-1">
                    {b.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-base md:text-lg text-foreground/85">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span className="leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                );
            }
          })}
        </div>
      </article>
    </SiteLayout>
  );
}
